import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardBody, CardHeader } from "reactstrap";
import getChartColorsArray from "Components/Common/ChartsDynamicColor";
import { getContractors } from "common/services/contractors.service";

const chartColors = ["--vz-danger", "--vz-success"];

const countParams = {
  pagination: {
    page: 1,
    pageSize: 1,
  },
  fields: ["id"],
};

const flaggedContractorsFilter = {
  $or: [
    {
      performanceRatings: {
        $and: [
          {
            isActive: true,
          },
          {
            performanceRatingType: {
              isFlag: true,
            },
          },
        ],
      },
    },
    {
      performanceRatings: {
        $and: [
          {
            isActive: true,
          },
          {
            performanceRatingType: {
              isDebarred: true,
            },
          },
        ],
      },
    },
  ],
};

const getTotal = (response: any) => response?.meta?.pagination?.total || 0;

const FlaggedContractorsPortfolio = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    flagged: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [totalResponse, flaggedResponse]: any = await Promise.all([
          getContractors(countParams),
          getContractors({
            ...countParams,
            filters: flaggedContractorsFilter,
          }),
        ]);

        setSummary({
          total: getTotal(totalResponse),
          flagged: getTotal(flaggedResponse),
        });
      } catch (error) {
        console.error("Error fetching flagged contractors summary", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const notFlagged = Math.max(summary.total - summary.flagged, 0);
  const flagRate =
    summary.total > 0 ? (summary.flagged / summary.total) * 100 : 0;

  const series = useMemo(
    () => [summary.flagged, notFlagged].filter((value) => value > 0),
    [notFlagged, summary.flagged]
  );

  const labels = useMemo(() => {
    const chartLabels = [];
    if (summary.flagged > 0) chartLabels.push("Flagged");
    if (notFlagged > 0) chartLabels.push("Not flagged");
    return chartLabels;
  }, [notFlagged, summary.flagged]);

  const colors = getChartColorsArray(JSON.stringify(chartColors));

  const options: any = {
    labels,
    chart: {
      height: 200,
      type: "pie",
    },
    colors:
      summary.flagged > 0
        ? colors
        : [colors[1], colors[0]].filter(Boolean),
    legend: {
      show: false,
    },
    stroke: {
      show: false,
    },
    dataLabels: {
      dropShadow: {
        enabled: false,
      },
      formatter: function (val: number) {
        return `${val?.toFixed(1) || 0}%`;
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return `${val.toLocaleString()} contractors`;
        },
      },
    },
  };

  const rows = [
    {
      label: "Flagged contractors",
      meta: "Flagged or debarred rating",
      value: summary.flagged,
      color: colors[0],
      icon: "ri-flag-2-line",
      textClass: "text-danger",
    },
    {
      label: "Not flagged",
      meta: "No flagged/debarred rating",
      value: notFlagged,
      color: colors[1],
      icon: "ri-shield-check-line",
      textClass: "text-success",
    },
    {
      label: "Flag rate",
      meta: "Of all contractors",
      value: `${flagRate.toFixed(1)}%`,
      color: "#f7b84b",
      icon: "ri-pulse-line",
      textClass: "text-warning",
    },
  ];

  return (
    <Card className="card-height-100">
      <CardHeader className="border-0 align-items-center d-flex">
        <h4 className="card-title mb-0 flex-grow-1">Flagged Contractors</h4>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: 200 }}
          >
            <span className="text-muted">Loading...</span>
          </div>
        ) : series.length === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: 200 }}
          >
            <span className="text-muted">No contractors available</span>
          </div>
        ) : (
          <ReactApexChart
            dir="ltr"
            className="apex-charts"
            options={options}
            series={series}
            type="pie"
            height={200}
          />
        )}

        <ul className="list-group list-group-flush border-dashed mb-0 mt-3 pt-2">
          {rows.map((item, index) => (
            <li
              className={`list-group-item px-0 ${
                index === rows.length - 1 ? "pb-0" : ""
              }`}
              key={item.label}
            >
              <div className="d-flex">
                <div className="flex-shrink-0 avatar-xs">
                  <span className="avatar-title bg-light p-1 rounded-circle material-shadow">
                    <i
                      className={item.icon}
                      style={{ color: item.color }}
                    ></i>
                  </span>
                </div>
                <div className="flex-grow-1 ms-2 overflow-hidden">
                  <h6 className="mb-1 text-truncate">{item.label}</h6>
                  <p className="fs-12 mb-0 text-muted">
                    <i
                      className="mdi mdi-circle fs-10 align-middle me-1"
                      style={{ color: item.color }}
                    ></i>
                    {item.meta}
                  </p>
                </div>
                <div className="flex-shrink-0 text-end">
                  <h6 className={`mb-1 ${item.textClass}`}>
                    {typeof item.value === "number"
                      ? item.value.toLocaleString()
                      : item.value}
                  </h6>
                  <p className="text-muted fs-12 mb-0">Current</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
};

export default FlaggedContractorsPortfolio;
